/**
 * HasPermission Component
 * Conditionally renders children based on user permissions.
 *
 * Usage:
 * <HasPermission permission="security:manage">
 *   <Button>Delete User</Button>
 * </HasPermission>
 *
 * With fallback:
 * <HasPermission permission="transactions:write" fallback={<span>View Only</span>}>
 *   <Button>Create Transaction</Button>
 * </HasPermission>
 */

import React from 'react';
import { useAuth } from '@/app/contexts/AuthContext';
import { hasPermission, hasAnyPermission, hasAllPermissions } from '@/app/utils/permissions';

interface HasPermissionProps {
  /** Single permission to check */
  permission?: string;
  /** Multiple permissions - user must have at least one (OR logic) */
  anyOf?: string[];
  /** Multiple permissions - user must have all (AND logic) */
  allOf?: string[];
  /** Content to render if user has the required permission(s) */
  children: React.ReactNode;
  /** Optional fallback content to render if user lacks permission(s) */
  fallback?: React.ReactNode;
}

const HasPermission: React.FC<HasPermissionProps> = ({
  permission,
  anyOf,
  allOf,
  children,
  fallback = null,
}) => {
  const { userProfile } = useAuth();

  // Determine if user has required permissions
  let hasRequiredPermission = false;

  if (permission) {
    hasRequiredPermission = hasPermission(userProfile, permission);
  } else if (anyOf && anyOf.length > 0) {
    hasRequiredPermission = hasAnyPermission(userProfile, anyOf);
  } else if (allOf && allOf.length > 0) {
    hasRequiredPermission = hasAllPermissions(userProfile, allOf);
  } else {
    // No permission requirement specified - render children
    hasRequiredPermission = true;
  }

  if (hasRequiredPermission) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

export default HasPermission;
